import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Mail, CheckCircle2, LogOut } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import logo from '../assets/noderiver-black.svg';

const DEFAULT_PROVIDERS = {
  magicLink: true,
  google: true,
  oidc: false,
};

const AuthPortal = ({
  open,
  mode = 'login',
  onClose,
  providers,
  onMagicLinkSubmit,
  magicLinkState,
  magicLinkPending = false,
  onProviderSelect,
  onOpenClassicLogin,
  oauthPendingProvider = null,
  logoutPending = false,
  onConfirmLogout,
  sessionUser,
}) => {
  const [email, setEmail] = useState('');
  const availableProviders = providers ?? DEFAULT_PROVIDERS;
  const isLogin = mode === 'login';
  const isProviderPending = (providerId) => oauthPendingProvider === providerId;

  useEffect(() => {
    if (!open) {
      setEmail('');
    }
  }, [open]);

  useEffect(() => {
    if (magicLinkState?.status === 'success' && magicLinkState.email) {
      setEmail(magicLinkState.email);
    }
  }, [magicLinkState]);

  const successCopy = useMemo(() => {
    if (magicLinkState?.status !== 'success' || !magicLinkState.email) return null;
    return `We emailed a secure link to ${magicLinkState.email}. Keep this window open and open the email in the same browser to finish signing in.`;
  }, [magicLinkState]);

  const errorCopy = magicLinkState?.status === 'error' ? magicLinkState.error : null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!onMagicLinkSubmit || !email?.trim()) return;
    await onMagicLinkSubmit(email.trim());
  };

  const handleOAuth = (providerId) => {
    if (!onProviderSelect) return;
    onProviderSelect(providerId);
  };

  const closePortal = (nextOpen) => {
    if (!nextOpen && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={closePortal}>
      <DialogContent className="max-w-[480px] w-full border-none bg-transparent p-0 shadow-none focus-visible:outline-none focus:outline-none">
        <div className="overflow-hidden rounded-[32px] border border-gray-100 bg-white shadow-2xl ring-1 ring-black/5">
          <div className="px-8 py-10 text-gray-900">
            <div className="flex justify-center mb-6">
              <img src={logo} alt="NodeRiver" className="h-10 w-10" />
            </div>

            {isLogin ? (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold leading-9 tracking-tight text-gray-900">Log in to BrandOn</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Access your brand workspace and AI assistants.
                  </p>
                </div>

                <div className="space-y-3">
                  {availableProviders?.google && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-center gap-3 rounded-2xl border-gray-200 bg-white py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                      onClick={() => handleOAuth('google')}
                      disabled={isProviderPending('google')}
                    >
                      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
                      {isProviderPending('google') ? 'Opening Google…' : 'Continue with Google'}
                    </Button>
                  )}

                  {availableProviders?.oidc && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-center gap-3 rounded-2xl border-gray-200 bg-white py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                      onClick={() => handleOAuth('oidc')}
                      disabled={isProviderPending('oidc')}
                    >
                      <ShieldCheck className="h-5 w-5 text-gray-500" />
                      {isProviderPending('oidc') ? 'Opening SSO…' : 'Continue with SSO'}
                    </Button>
                  )}
                </div>

                {availableProviders?.magicLink && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Or continue with email</span>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div className="space-y-2">
                        <label htmlFor="brandon-auth-email" className="sr-only">
                          Work email
                        </label>
                        <Input
                          id="brandon-auth-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@company.com"
                          required
                          className="rounded-xl border-gray-300"
                        />
                      </div>
                      {errorCopy && (
                        <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">
                          {errorCopy}
                        </p>
                      )}
                      {successCopy && (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                            <span>{successCopy}</span>
                          </div>
                        </div>
                      )}
                      <Button
                        type="submit"
                        className="w-full justify-center rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white hover:bg-gray-800"
                        disabled={magicLinkPending}
                      >
                        {magicLinkPending ? 'Sending magic link…' : 'Email me a magic link'}
                      </Button>
                    </form>
                  </>
                )}

                <div className="mt-6 text-center text-xs text-gray-500">
                  <button
                    type="button"
                    onClick={onOpenClassicLogin}
                    className="font-semibold text-gray-900 hover:underline"
                  >
                    Open full NodeRiver login
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col justify-between space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold leading-9 tracking-tight text-gray-900">Sign out</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Are you sure you want to sign out?
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-600">
                      <LogOut className="h-5 w-5" />
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-sm font-medium text-gray-900 truncate">{sessionUser?.name || sessionUser?.email || 'Current session'}</p>
                      <p className="text-xs text-gray-500 truncate">{sessionUser?.email}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    type="button"
                    className="w-full justify-center rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white hover:bg-gray-800"
                    onClick={onConfirmLogout}
                    disabled={logoutPending}
                  >
                    {logoutPending ? 'Signing you out…' : 'Sign out'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-center rounded-xl border border-gray-200 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    onClick={onClose}
                    disabled={logoutPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthPortal;

